import { Game, MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import { cn } from "@win-reality/react-ui";
import { useEffect, useState } from "react";

import {
  formatEventDateTime,
  formatTimeRemaining,
  getEventTimes,
  getTeamNames,
} from "@/utils/eventUtils";

import BatIcon from "../../../assets/bat.svg";
import LiveFieldStatusIcon from "../../../assets/live-field-status-icon.svg";
import { Source } from "../SingleField/Source";

type FieldEventProps = {
  event?: Game;
  field: MachinePublicHydrated;
  source?: string;
};

function useTimeRemaining(endTime: number | undefined): string {
  const [text, setText] = useState(() =>
    endTime ? formatTimeRemaining(endTime) : "N/A",
  );
  useEffect(() => {
    if (!endTime) {
      setText("N/A");
      return;
    }
    setText(formatTimeRemaining(endTime));
    if (endTime <= Date.now()) return;
    const id = setInterval(() => {
      setText(formatTimeRemaining(endTime));
      if (endTime <= Date.now()) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [endTime]);
  return text;
}

function FieldCardEvent({ event, source }: FieldEventProps) {
  const timeRemaining = useTimeRemaining(
    event ? getEventTimes(event).endTime : undefined,
  );

  if (!event) {
    return <div>No event data available</div>;
  }

  const homeTeamName = getTeamNames(event).homeTeamName;
  const awayTeamName = getTeamNames(event).awayTeamName;

  const startTime = getEventTimes(event).startTime;
  const endTime = getEventTimes(event).endTime;

  const isLive = !endTime || endTime > Date.now();

  if (event.type === "game") {
    return (
      <div className="flex flex-col gap-4">
        <div className="text-sm grid grid-cols-2 gap-1.5">
          <div className="px-2 py-1 border border-gray-200 rounded-sm">
            <div className="overflow-hidden whitespace-nowrap  text-ellipsis">
              {homeTeamName}
            </div>

            {/* <div className="text-xs text-gray-400">
              {event.homeTeam.roster ? `${event.homeTeam.roster.name}` : null}
            </div> */}
          </div>

          <div className="px-2 py-1 border border-gray-200 rounded-sm">
            <div className="overflow-hidden whitespace-nowrap  text-ellipsis">
              {awayTeamName}
            </div>
            {/* <div className="text-xs text-gray-400">
              {event.awayTeam.roster ? `${event.awayTeam.roster.name}` : null}
            </div> */}
          </div>
        </div>

        <div className="flex justify-between">
          <div className="flex items-center gap-2 text-gray-400 text-xs">
            {isLive ? (
              <>
                <img
                  src={LiveFieldStatusIcon}
                  alt="Live Field Status"
                  className="h-4 w-auto"
                />
                Live Now · Time Remaining: {timeRemaining}
              </>
            ) : (
              <span>{formatEventDateTime(startTime)}</span>
            )}
          </div>

          <div className="flex gap-2 items-center">
            {/* <StrikeZonePreview config={event.strikeZoneConfig} /> */}

            <Source source={source} />
          </div>
        </div>
      </div>
    );
  }

  if (event.type === "batting_practice" || event.type === "bullpen") {
    return (
      <div className="flex flex-col gap-4">
        <div
          className={cn(
            isLive && "px-2 py-2 border border-gray-200 rounded-sm",
          )}
        >
          <div className="flex gap-4 items-center">
            <img src={BatIcon} alt="Practice Event" className="h-7 w-auto" />

            <div>
              <div className="text-sm">Practice</div>
              <div className="text-xs text-gray-400">
                {formatEventDateTime(startTime)}
              </div>
              {/* <div className="text-xs text-gray-400">
                {event.additionalNotes}
              </div> */}
            </div>
          </div>
        </div>

        {isLive && (
          <div className="flex justify-between">
            <div className="flex items-center gap-2 text-gray-400 text-xs">
              <>
                <img
                  src={LiveFieldStatusIcon}
                  alt="Live Field Status"
                  className="h-4 w-auto"
                />
                Live Now · Time Remaining: {timeRemaining}
              </>
            </div>

            <div className="flex gap-2 items-center">
              {/* <StrikeZonePreview config={event.strikeZoneConfig} />
              <Scoring scoring={event.scoring} /> */}
              <Source source={source} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div>Unknown event type</div>;
}

export { FieldCardEvent };
